import "@/App.css";
import React, { useState, useEffect, useRef } from "react";
import { BrowserRouter as Router, Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import { motion, useInView, AnimatePresence } from "framer-motion";
import {
  ArrowUpRight,
  ChevronDown,
  ChevronRight,
  Search,
  Palette,
  Code2,
  Plus,
  Minus,
  Linkedin,
  Instagram,
  Mail,
  ExternalLink,
  LayoutGrid,
  MessageSquare,
  Zap
} from "lucide-react";
import { Toaster } from "sonner";
import SaaSPage from "./pages/SaaSPage";
import B2BPage from "./pages/B2BPage";
import StartupsPage from "./pages/StartupsPage";
import EcommercePage from "./pages/EcommercePage";
import StrategyPage from "./pages/StrategyPage";

// ============================================
// PREMIUM COLOR TOKENS
// ============================================
const COLORS = {
  gradientOrange: '#FF5A1F',
  gradientCrimson: '#8B1E3F',
  accentOrange: '#FF5A1F',
  textHeading: '#F8F8F8',
  textBody: '#A0A0A0',
  textMuted: '#6B6B6B',
  backgroundDeep: '#0B0B0D',
  backgroundElevated: '#111111',
  backgroundCard: '#161616',
  champagne: '#E6C79C',
};

// Project mockup images
const MOCKUP_IMAGES = [
  "https://images.unsplash.com/photo-1664748512003-8820b08d8161?crop=entropy&cs=srgb&fm=jpg&w=800&q=80",
  "https://images.unsplash.com/photo-1620287341056-49a2f1ab2fdc?crop=entropy&cs=srgb&fm=jpg&w=800&q=80",
  "https://images.pexels.com/photos/811587/pexels-photo-811587.jpeg?auto=compress&cs=tinysrgb&w=800",
  "https://images.unsplash.com/photo-1615540127498-12c3049eded0?crop=entropy&cs=srgb&fm=jpg&w=800&q=80",
  "https://images.pexels.com/photos/7191162/pexels-photo-7191162.jpeg?auto=compress&cs=tinysrgb&w=800",
  "https://images.unsplash.com/photo-1706700392626-5279fb90ae73?crop=entropy&cs=srgb&fm=jpg&w=800&q=80",
];

const getIsMobile = () => typeof window !== 'undefined' && window.innerWidth < 768;

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: getIsMobile() ? 20 : 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: getIsMobile() ? 0.5 : 0.7,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: getIsMobile() ? 0.08 : 0.12,
      delayChildren: getIsMobile() ? 0.1 : 0.2
    }
  }
};

// Animated section wrapper
const AnimatedSection = ({ children, className = "", immediate = false }) => {
  const ref = useRef(null);
  // Trigger earlier on mobile so text doesn't pop in late while scrolling down
  const isInView = useInView(ref, { once: true, margin: getIsMobile() ? "0px" : "-100px" });

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      // If immediate is true, don't wait for IntersectionObserver (fixes Hero section load lag)
      animate={immediate || isInView ? "visible" : "hidden"}
      variants={staggerContainer}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// ===== NAVBAR =====
const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navigate = useNavigate();
  const location = useLocation();

  const handleNavClick = (id) => {
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const industries = [
    { name: "SaaS Platforms", path: "/saas" },
    { name: "B2B Companies", path: "/b2b" },
    { name: "High-Growth Startups", path: "/startups" },
    { name: "E-commerce Brands", path: "/ecommerce" }
  ];

  return (
    <motion.nav
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className={`fixed top-0 left-0 right-0 z-50 px-6 py-4 transition-all duration-500 ${scrolled ? 'navbar-glass' : ''
        }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between w-full">
        {/* Mobile Logo & CTA Container (Visible on SM only) */}
        <div className="flex md:hidden items-center justify-between w-full gap-4">
          <Link to="/" className="flex items-center" data-testid="nav-logo-mobile">
            <img src="/logo.png" alt="Aligned Studio" className="h-14 w-auto object-contain drop-shadow-lg" />
          </Link>
          <button
            onClick={() => navigate("/strategy")}
            className="cta-premium px-5 py-2 text-white text-sm font-semibold rounded-full shadow-lg shadow-[#FF5A1F]/20"
          >
            Get a Strategy
          </button>
        </div>

        {/* Desktop Logo */}
        <Link to="/" className="hidden md:flex items-center gap-4" data-testid="nav-logo-desktop">
          <img src="/logo.png" alt="Aligned Studio" className="h-16 md:h-20 w-auto object-contain drop-shadow-xl" />
          <span className="text-white font-semibold text-xl tracking-tighter uppercase font-sans tracking-[0.05em] drop-shadow-md">Aligned Studio</span>
        </Link>

        {/* Center Nav */}
        <div className="hidden md:flex items-center gap-8">
          {[
            { label: "About", id: "about" },
            { label: "Portfolio", id: "portfolio" },
            { label: "FAQs", id: "faq" }
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => handleNavClick(item.id)}
              className="text-sm text-[#A0A0A0] hover:text-[#F8F8F8] transition-colors duration-300"
              data-testid={`nav-${item.id}`}
            >
              {item.label}
            </button>
          ))}

          {/* Industries Dropdown */}
          <div className="relative dropdown" onMouseEnter={() => setDropdownOpen(true)} onMouseLeave={() => setDropdownOpen(false)}>
            <button className="flex items-center gap-1 text-sm text-[#A0A0A0] hover:text-[#F8F8F8] transition-colors" data-testid="nav-industries">
              Industries
              <ChevronDown className="w-4 h-4" />
            </button>
            <div className={`dropdown-menu-premium absolute top-full left-0 mt-3 w-48 rounded-xl p-2 ${dropdownOpen ? 'opacity-100 visible translate-y-0' : ''}`}>
              {industries.map((ind) => (
                <Link
                  key={ind.name}
                  to={ind.path}
                  onClick={() => setDropdownOpen(false)}
                  className="block w-full text-left px-4 py-2.5 text-sm text-[#A0A0A0] hover:text-[#F8F8F8] hover:bg-white/[0.03] rounded-lg transition-all"
                >
                  {ind.name}
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Desktop CTA (Right) */}
        <button
          onClick={() => navigate("/strategy")}
          className="hidden md:block cta-premium px-6 py-2.5 text-white text-sm font-semibold rounded-full"
          data-testid="nav-cta-desktop"
        >
          Get Strategy
        </button>
      </div>
    </motion.nav>
  );
};

// ===== HERO SECTION =====
const HeroSection = () => {
  return (
    <section id="hero" className="min-h-screen flex items-center pt-32 pb-24 px-6" data-testid="hero-section">
      <div className="max-w-7xl mx-auto w-full">
        {/* Hero Card with Cinematic Gradient */}
        <div className="hero-card-premium rounded-[24px] p-8 sm:p-12 md:p-20 min-h-[75vh] md:min-h-[85vh] flex flex-col justify-center relative overflow-hidden">
          {/* Subtle Grain Overlay */}
          <div className="bg-grain" />

          <AnimatedSection immediate={true}>
            <div className="max-w-4xl relative z-10">
              <motion.p variants={fadeInUp} className="text-[#FF6A2A] text-sm md:text-base font-semibold tracking-[0.2em] uppercase mb-8">
                Strategic Web Design Agency
              </motion.p>

              <motion.h1
                variants={fadeInUp}
                className="text-4xl sm:text-5xl md:text-6xl lg:text-[4.5rem] xl:text-[5.5rem] max-w-5xl font-extrabold text-[#F2F2F2] leading-[1.05] tracking-[-0.03em] mb-10 font-sans uppercase glow-text-diffusion"
                data-testid="hero-headline"
              >
                We Turn Website Visitors Into Real Leads
              </motion.h1>

              <motion.p variants={fadeInUp} className="text-lg md:text-xl lg:text-2xl text-[#9A9A9A] max-w-3xl mb-14 leading-relaxed font-light">
                We build websites that convert visitors into customers. Clear messaging, intentional design, and long-term growth — not just pretty pages.
              </motion.p>

              {/* CTAs */}
              <motion.div variants={fadeInUp} className="flex flex-wrap items-center gap-6 mb-16">
                <button
                  onClick={() => document.getElementById("cta-final")?.scrollIntoView({ behavior: "smooth" })}
                  className="cta-premium px-8 py-4 text-white font-semibold rounded-[20px] flex items-center justify-center gap-2 w-full sm:w-auto"
                  data-testid="hero-cta-primary"
                >
                  Book a Strategy Call
                  <ArrowUpRight className="w-5 h-5" />
                </button>
                <button
                  onClick={() => document.getElementById("portfolio")?.scrollIntoView({ behavior: "smooth" })}
                  className="cta-outline px-8 py-4 text-[#F5F5F2] font-medium rounded-[20px] flex items-center justify-center gap-2 w-full sm:w-auto"
                  data-testid="hero-cta-secondary"
                >
                  View Our Work
                  <ChevronRight className="w-5 h-5" />
                </button>
              </motion.div>

              {/* Service Tags */}
              <motion.div variants={fadeInUp} className="flex flex-wrap gap-3 mt-4">
                {[
                  "Web Architecture",
                  "SaaS Engineering",
                  "Search Optimization",
                  "Growth Systems"
                ].map((service, idx) => (
                  <div key={idx} className="flex items-center gap-2.5 px-4 py-2 rounded-full border border-white/[0.05] bg-[#111111]/50 backdrop-blur-md transition-colors hover:border-[#FF4A00]/30 hover:bg-[#FF4A00]/5">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#FF4A00] shadow-[0_0_8px_rgba(255,74,0,0.6)]" />
                    <span className="text-[#A0A0A0] text-sm md:text-base font-medium tracking-wide">{service}</span>
                  </div>
                ))}
              </motion.div>
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
};

// Removed TrustLayer at user request

// ===== SCROLLING WORK MARQUEE =====
const WorkMarquee = () => {
  const trustIndicators = [
    { value: "Strategy-First", label: "Approach to Every Project" },
    { value: "Results-Driven", label: "Design That Converts" },
    { value: "Long-Term", label: "Partnership Mindset" }
  ];

  return (
    <section className="py-20 overflow-hidden section-dark" data-testid="marquee-section">
      {/* Marquee Row 1 - Left */}
      <div className="marquee-container overflow-hidden mb-4">
        <div className="flex animate-marquee-left w-max flex-nowrap">
          {/* Reduced duplicates on mobile for memory savings */}
          {[...MOCKUP_IMAGES, ...MOCKUP_IMAGES].map((img, i) => (
            <div key={i} className="flex-shrink-0 w-[280px] sm:w-[320px] md:w-[420px] mx-3">
              <div className="rounded-[20px] overflow-hidden border border-white/[0.04] bg-[#111111] transform-gpu">
                <img src={img} alt="" loading="lazy" decoding="async" className="w-full h-52 md:h-60 object-cover img-bw-premium hover:filter-none transition-all duration-700 hover:scale-105" />
              </div>
            </div>
          ))}
          <div className="hidden md:flex flex-nowrap">
            {[...MOCKUP_IMAGES, ...MOCKUP_IMAGES].map((img, i) => (
              <div key={`desktop-1-${i}`} className="flex-shrink-0 w-[280px] sm:w-[320px] md:w-[420px] mx-3">
                <div className="rounded-[20px] overflow-hidden border border-white/[0.04] bg-[#111111] transform-gpu">
                  <img src={img} alt="" loading="lazy" decoding="async" className="w-full h-52 md:h-60 object-cover img-bw-premium hover:filter-none transition-all duration-700 hover:scale-105" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Marquee Row 2 - Right */}
      <div className="marquee-container overflow-hidden mb-14">
        <div className="flex animate-marquee-right w-max flex-nowrap">
          {[...MOCKUP_IMAGES.slice().reverse(), ...MOCKUP_IMAGES.slice().reverse()].map((img, i) => (
            <div key={i} className="flex-shrink-0 w-[280px] sm:w-[320px] md:w-[420px] mx-3">
              <div className="rounded-[20px] overflow-hidden border border-white/[0.04] bg-[#111111] transform-gpu">
                <img src={img} alt="" loading="lazy" decoding="async" className="w-full h-52 md:h-60 object-cover img-bw-premium hover:filter-none transition-all duration-700 hover:scale-105" />
              </div>
            </div>
          ))}
          <div className="hidden md:flex flex-nowrap">
            {[...MOCKUP_IMAGES.slice().reverse(), ...MOCKUP_IMAGES.slice().reverse()].map((img, i) => (
              <div key={`desktop-2-${i}`} className="flex-shrink-0 w-[280px] sm:w-[320px] md:w-[420px] mx-3">
                <div className="rounded-[20px] overflow-hidden border border-white/[0.04] bg-[#111111] transform-gpu">
                  <img src={img} alt="" loading="lazy" decoding="async" className="w-full h-52 md:h-60 object-cover img-bw-premium hover:filter-none transition-all duration-700 hover:scale-105" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Trust Indicators */}
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-wrap justify-center gap-4" data-testid="trust-badges">
          {trustIndicators.map((item, i) => (
            <div key={i} className="stat-badge-premium px-6 py-3.5 rounded-full flex items-center gap-3">
              <span className="text-[#FF5A1F] font-bold">{item.value}</span>
              <span className="text-[#6B6B6B] text-sm">{item.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ===== TECH STACK BAR =====
const TechStackBar = () => {
  const technologies = [
    { name: "React", icon: "⚛️" },
    { name: "Tailwind CSS", icon: "🌊" },
    { name: "Framer Motion", icon: "✨" },
    { name: "Node.js", icon: "🟢" },
    { name: "Figma", icon: "🎨" },
    { name: "Vercel", icon: "▲" },
  ];

  return (
    <section className="py-16 border-y border-white/[0.04] section-elevated" data-testid="tech-stack-section">
      <div className="max-w-7xl mx-auto px-6 mb-8">
        <p className="text-left text-[#6B6B6B] text-sm tracking-wide">
          Built with Modern Technologies
        </p>
      </div>

      <div className="marquee-container overflow-hidden">
        <div className="flex animate-marquee-slow">
          {[...technologies, ...technologies, ...technologies].map((tech, i) => (
            <div key={i} className="flex-shrink-0 mx-14 flex items-center gap-2.5 opacity-35 hover:opacity-100 transition-opacity duration-300">
              <div className="w-9 h-9 rounded-lg bg-white/[0.06] flex items-center justify-center">
                <span className="text-[#F8F8F8] font-bold text-sm">{tech.icon}</span>
              </div>
              <span className="text-[#F8F8F8] font-medium">{tech.name}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ===== PROBLEM SECTION =====
const ProblemSection = () => {
  const navigate = useNavigate();
  const painPoints = [
    "Unclear messaging that confuses visitors",
    "Weak positioning indistinguishable from competitors",
    "Poor user flow with high friction",
    "No structured conversion architecture"
  ];

  return (
    <section id="about" className="py-28 md:py-36 px-6 section-dark" data-testid="problem-section">
      <div className="max-w-7xl mx-auto">
        <AnimatedSection>
          <div className="grid md:grid-cols-2 gap-12 md:gap-20 items-start">
            <div>
              <motion.p variants={fadeInUp} className="text-[#FF5A1F] text-sm font-semibold tracking-[0.15em] uppercase mb-5">
                The Friction
              </motion.p>
              <motion.h2
                variants={fadeInUp}
                className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-[1.1] tracking-[-0.02em] uppercase font-sans mb-8"
                data-testid="problem-headline"
              >
                Pretty interfaces are common. Strategic design that commands attention is rare.
              </motion.h2>
            </div>

            <div>
              <motion.p variants={fadeInUp} className="text-[#A0A0A0] text-lg leading-relaxed mb-10">
                The problem isn't design. It's strategy. Without a clear message and intentional structure, even the most beautiful website won't convert. Here's what we see over and over:
              </motion.p>

              <motion.ul variants={fadeInUp} className="space-y-5 mb-10">
                {painPoints.map((point, i) => (
                  <li key={i} className="flex items-start gap-4 text-[#A0A0A0]">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#FF5A1F] mt-2.5 flex-shrink-0" />
                    {point}
                  </li>
                ))}
              </motion.ul>

              <motion.button
                variants={fadeInUp}
                onClick={() => navigate("/strategy")}
                className="cta-premium px-8 py-4 text-white font-semibold rounded-[20px] inline-flex items-center justify-center gap-2 w-full sm:w-auto"
              >
                Get in touch
                <ArrowUpRight className="w-5 h-5" />
              </motion.button>
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

// ===== INDUSTRIES SECTION =====
const IndustriesSection = () => {
  const navigate = useNavigate();

  const targetIndustries = [
    {
      id: "saas",
      title: "SaaS Platforms",
      subtitle: "Turn your marketing site into your best-performing sales rep.",
      description: "For SaaS companies, your website is your software's absolute first impression. We architect SaaS marketing sites designed to reduce churn, lower CPA, and perfectly communicate complex value propositions.",
      icon: "⚡",
      image: MOCKUP_IMAGES[0],
      path: "/saas"
    },
    {
      id: "b2b",
      title: "B2B Service Companies",
      subtitle: "Command Authority in the Enterprise Space.",
      description: "Transform your online presence into a 24/7 sales asset. We build B2B websites that establish immediate trust with C-suite decision-makers and generate highly qualified inbound leads.",
      icon: "🏢",
      image: MOCKUP_IMAGES[1],
      path: "/b2b"
    },
    {
      id: "startups",
      title: "High-Growth Startups",
      subtitle: "Secure Funding & Talent with a World-Class Presence.",
      description: "Startups move fast. We build scalable, high-impact web infrastructure that looks like you've already raised your Series B—attracting top talent, securing investment, and building immediate market authority.",
      icon: "🚀",
      image: MOCKUP_IMAGES[2],
      path: "/startups"
    },
    {
      id: "ecommerce",
      title: "E-commerce Brands",
      subtitle: "Maximize AOV and Conversion Rates at Scale.",
      description: "Every second and every click counts. We engineer high-performance e-commerce experiences with optimized user journeys, lightning-fast load times, and strategic layouts designed specifically to maximize revenue.",
      icon: "🛍️",
      image: MOCKUP_IMAGES[3],
      path: "/ecommerce"
    }
  ];

  return (
    <section className="py-28 md:py-36 px-6 section-dark" data-testid="industries-section">
      <div className="max-w-7xl mx-auto">
        <AnimatedSection>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-20">
            <div className="max-w-2xl">
              <motion.p variants={fadeInUp} className="text-[#FF5A1F] text-sm font-semibold tracking-[0.15em] uppercase mb-5">
                Who We Serve
              </motion.p>
              <motion.h2
                variants={fadeInUp}
                className="text-3xl md:text-4xl lg:text-5xl font-bold text-white tracking-[-0.02em] font-sans uppercase leading-[1.1]"
              >
                Specialized expertise for ambitious digital businesses.
              </motion.h2>
            </div>
            <motion.div variants={fadeInUp} className="md:text-right">
              <p className="text-[#A0A0A0] leading-relaxed max-w-md text-left md:text-right">
                We don't build generic brochure sites. We architect highly-specialized growth engines for these core sectors.
              </p>
            </motion.div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {targetIndustries.map((industry, i) => (
              <motion.div
                key={i}
                variants={fadeInUp}
                onClick={() => navigate(industry.path)}
                className="project-card-premium rounded-[20px] bg-[#111111] border border-white/[0.04] flex flex-col group cursor-pointer overflow-hidden transition-all duration-500 hover:-translate-y-2 hover:border-[#FF5A1F]/30 shadow-xl"
              >
                {/* Image Section */}
                <div className="relative h-48 md:h-64 w-full overflow-hidden">
                  <div className="absolute inset-0 bg-[#0B0B0D]/40 group-hover:bg-transparent transition-colors duration-500 z-10" />
                  <img src={industry.image} alt={industry.title} loading="lazy" className="w-full h-full object-cover grayscale opacity-80 group-hover:grayscale-0 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700" />
                </div>

                {/* Content Section */}
                <div className="p-8 md:p-10 flex flex-col flex-1">
                  <div className="flex items-center justify-between mb-4">
                    <div className="text-4xl">{industry.icon}</div>
                    <ArrowUpRight className="w-6 h-6 text-white/20 group-hover:text-[#FF5A1F] transition-colors duration-300" />
                  </div>
                  <h3 className="text-2xl font-bold text-[#F8F8F8] mb-3 group-hover:text-[#FF4A00] transition-colors duration-300 flex items-center gap-2">
                    {industry.title}
                  </h3>
                  <p className="text-[#A0A0A0] leading-relaxed text-base flex-1">
                    {industry.description}
                  </p>

                  {/* Subtle Link at bottom of card */}
                  <div className="mt-8 pt-6 border-t border-white/5 font-semibold text-sm tracking-wide text-[#F8F8F8] uppercase flex items-center gap-2 group-hover:text-[#FF5A1F] transition-colors">
                    Explore {industry.title} <ChevronRight className="w-4 h-4" />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

// ===== SERVICES SECTION =====
// ===== SOLUTION SECTION =====
const SolutionSection = () => {
  const benefits = [
    {
      icon: Search,
      title: "Clear Positioning",
      description: "You get a site that clearly articulates what you do and who it's for, instantly."
    },
    {
      icon: LayoutGrid,
      title: "Conversion-Focused Layout",
      description: "You get strategic wireframes engineered to guide users naturally to the signup page."
    },
    {
      icon: MessageSquare,
      title: "Strategic Messaging",
      description: "You get copy that speaks directly to your ideal customer's pain points and desired outcomes."
    },
    {
      icon: Zap,
      title: "Performance Optimization",
      description: "You get lightning-fast load times and SEO-ready structure securing maximum visibility."
    }
  ];

  return (
    <section className="py-28 md:py-36 px-6 section-elevated" data-testid="solution-section">
      <div className="max-w-7xl mx-auto">
        <AnimatedSection>
          <motion.p variants={fadeInUp} className="text-[#FF5A1F] text-sm font-semibold tracking-[0.15em] uppercase mb-5 text-center">
            The Solution
          </motion.p>
          <motion.h2
            variants={fadeInUp}
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-white text-center max-w-3xl mx-auto mb-20 tracking-[-0.02em] font-sans uppercase leading-[1.1]"
          >
            We Build Websites Engineered for Growth
          </motion.h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="solution-grid">
            {benefits.map((benefit, i) => (
              <motion.div
                key={i}
                variants={fadeInUp}
                className="service-card-premium p-8 rounded-[20px]"
                data-testid={`benefit-card-${i + 1}`}
              >
                <div className="w-14 h-14 rounded-[14px] icon-glow flex items-center justify-center mb-6">
                  <benefit.icon className="w-6 h-6 text-[#FF5A1F]" />
                </div>
                <h3 className="text-xl font-bold text-white mb-4 tracking-tight">{benefit.title}</h3>
                <p className="text-[#A0A0A0] leading-relaxed text-sm">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

// ===== PROCESS SECTION =====
const ProcessSection = () => {
  const approaches = [
    {
      step: "01",
      title: "Audit & Strategy",
      description: "We analyze your current metrics, identify conversion completely gaps, and map out a strategic blueprint."
    },
    {
      step: "02",
      title: "Messaging & Structure",
      description: "We craft compelling, outcome-driven copy and wireframe a user journey designed to maximize signups."
    },
    {
      step: "03",
      title: "Design & Development",
      description: "We build a premium, high-performance website optimized for fast loading and scalable growth."
    },
    {
      step: "04",
      title: "Launch & Optimization",
      description: "We deploy the site, monitor user behavior, and apply data-driven tweaks for ongoing conversion improvement."
    }
  ];

  return (
    <section id="process" className="py-28 md:py-36 px-6 section-dark" data-testid="process-section">
      <div className="max-w-7xl mx-auto">
        <AnimatedSection>
          <motion.p variants={fadeInUp} className="text-[#FF5A1F] text-sm font-semibold tracking-[0.15em] uppercase mb-5">
            How We Work
          </motion.p>
          <motion.h2
            variants={fadeInUp}
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-white max-w-3xl mb-20 tracking-[-0.02em] font-sans uppercase leading-[1.1]"
          >
            A Systematic Framework for Scalable Growth
          </motion.h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="process-grid">
            {approaches.map((approach, i) => (
              <motion.div
                key={i}
                variants={fadeInUp}
                className="project-card-premium p-8 rounded-[16px] border border-white/[0.04] bg-[#111111]"
              >
                <div className="text-[#FF5A1F] text-3xl font-bold mb-6 font-mono">{approach.step}</div>
                <h3 className="text-lg font-bold text-[#F8F8F8] mb-3">{approach.title}</h3>
                <p className="text-[#A0A0A0] text-sm leading-relaxed">{approach.description}</p>
              </motion.div>
            ))}
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

// ===== MID-PAGE CTA =====
const MidPageCTA = () => {
  const navigate = useNavigate();
  return (
    <section className="py-28 md:py-36 px-6 section-elevated ambient-glow" data-testid="mid-cta-section">
      <div className="max-w-7xl mx-auto">
        <AnimatedSection>
          <div className="grid md:grid-cols-2 gap-16 md:gap-24 items-center">
            {/* Left Side: Headline */}
            <div>
              <motion.h2
                variants={fadeInUp}
                className="text-3xl md:text-4xl lg:text-5xl lg:text-[3.5rem] font-bold text-white tracking-[-0.02em] font-sans uppercase leading-[1.05]"
              >
                A better website starts with the right strategy.
              </motion.h2>
            </div>

            {/* Right Side: Description and CTA */}
            <div className="flex flex-col items-start">
              <motion.p variants={fadeInUp} className="text-[#A0A0A0] text-lg md:text-xl mb-10 leading-relaxed max-w-lg">
                Before we touch design or code, we understand your goals, your audience, and your challenges. That's how we build websites that actually work.
              </motion.p>
              <motion.button
                variants={fadeInUp}
                onClick={() => navigate("/strategy")}
                className="cta-premium px-8 py-4 md:px-10 md:py-5 text-white text-lg font-semibold rounded-[24px] inline-flex items-center justify-center gap-2 w-full sm:w-auto"
                data-testid="mid-cta-button"
              >
                Start Your Project
                <ArrowUpRight className="w-5 h-5" />
              </motion.button>
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

// Removed old ProcessSection to resolve naming conflicts

// ===== FAQ SECTION =====
const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    { q: "Do you use templates?", a: "Everything is custom. We don't use templates. Every design is built from scratch based on your brand." },
    { q: "How many revisions?", a: "We include 2 rounds of revisions per phase. Most projects don't need more because we align closely." },
    { q: "Match existing brand?", a: "Absolutely. We work within your brand guidelines or help evolve them if needed." },
    { q: "How do you do SEO?", a: "SEO is baked into our process — clean code, fast load times, proper heading structure." },
    { q: "Work with clients globally?", a: "Yes! We work with clients across North America, Europe, and beyond." },
    { q: "Communication process?", a: "We use Slack for day-to-day updates and weekly video calls for reviews." },
    { q: "Why Webflow?", a: "Webflow offers cleaner code, better performance, and easier updates without plugins." },
    { q: "What do you need from us?", a: "Brand assets, content direction, and access to stakeholders. We provide a checklist." },
    { q: "Custom integrations?", a: "Yes — we integrate with tools like HubSpot, Salesforce, Zapier, and custom APIs." },
  ];

  return (
    <section id="faq" className="py-28 md:py-36 px-6 section-elevated" data-testid="faq-section">
      <div className="max-w-[90rem] mx-auto">
        <AnimatedSection>
          <div className="flex flex-col items-center text-center">
            <motion.p variants={fadeInUp} className="text-[#FF5A1F] text-sm font-semibold tracking-[0.15em] uppercase mb-5">
              FAQs
            </motion.p>
            <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-bold text-white mb-16 tracking-[-0.02em] font-sans uppercase leading-[1.1] max-w-3xl">
              Quick answers to common questions we hear from founders.
            </motion.h2>
          </div>

          <motion.div variants={fadeInUp} className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="faq-list">
            {faqs.map((faq, i) => (
              <div key={i} className="faq-item-premium rounded-xl overflow-hidden self-start h-full flex flex-col justify-start">
                <button
                  onClick={() => setOpenIndex(openIndex === i ? null : i)}
                  className="w-full flex items-start justify-between px-6 py-5 text-left h-full"
                  data-testid={`faq-item-${i + 1}`}
                >
                  <span className="text-[#F8F8F8] font-medium pr-4 pt-0.5 leading-snug">{faq.q}</span>
                  {openIndex === i ? (
                    <Minus className="w-5 h-5 text-[#FF4A00] flex-shrink-0 mt-0.5" />
                  ) : (
                    <Plus className="w-5 h-5 text-[#6B6B6B] flex-shrink-0 mt-0.5" />
                  )}
                </button>
                <AnimatePresence>
                  {openIndex === i && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25 }}
                    >
                      <p className="px-6 pb-6 text-[#A0A0A0] text-sm leading-relaxed border-t border-white/[0.04] pt-4 mt-2 mx-6">
                        {faq.a}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </motion.div>
        </AnimatedSection>
      </div>
    </section>
  );
};

// Removed CaseStudySection per user request

// ===== QUALITY PROCESS SECTION =====
const QualityProcessSection = () => {
  const features = [
    { title: "Live Project Portal", description: "Track progress in real-time. See every milestone, feedback round, and deliverable in one centralized dashboard.", image: MOCKUP_IMAGES[4] },
    { title: "Proven Frameworks", description: "Refined across 60+ projects. Our process eliminates guesswork and ensures consistent, high-quality results.", image: MOCKUP_IMAGES[5] },
    { title: "Rigorous 70-Point QA", description: "Every project goes through comprehensive quality assurance. Nothing ships until it's pixel-perfect.", image: MOCKUP_IMAGES[0] }
  ];

  return (
    <section className="py-28 md:py-36 px-6 section-dark" data-testid="quality-process-section">
      <div className="max-w-7xl mx-auto">
        <AnimatedSection>
          <motion.p variants={fadeInUp} className="text-[#FF5A1F] text-sm font-semibold tracking-[0.15em] uppercase mb-5">
            Our Process
          </motion.p>
          <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl lg:text-5xl font-bold text-white max-w-3xl mb-20 tracking-[-0.02em] font-sans uppercase leading-[1.1]">
            Built for Quality, Transparency, and Results.
          </motion.h2>

          <div className="space-y-12 md:space-y-10">
            {features.map((feature, i) => (
              <motion.div key={i} variants={fadeInUp} className={`grid md:grid-cols-2 gap-8 md:gap-10 items-center ${i % 2 === 1 ? 'md:flex-row-reverse' : ''}`}>
                <div className={i % 2 === 1 ? 'md:order-2' : ''}>
                  <h3 className="text-2xl font-bold text-[#F8F8F8] mb-4">{feature.title}</h3>
                  <p className="text-[#A0A0A0] leading-relaxed">{feature.description}</p>
                </div>
                <div className={`rounded-[16px] overflow-hidden border border-white/[0.04] ${i % 2 === 1 ? 'md:order-1' : ''}`}>
                  <img src={feature.image} alt={feature.title} className="w-full h-64 object-cover img-bw-premium hover:filter-none transition-all duration-500" />
                </div>
              </motion.div>
            ))}
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

// ===== PORTFOLIO / VIEW OUR WORK SECTION =====
const PortfolioSection = () => {
  const portfolios = [
    { title: "SaaS Dashboard Concept", url: "#", image: MOCKUP_IMAGES[4] },
    { title: "Fitness Spark Forge", url: "https://fitness-spark-forge.vercel.app/", image: "/portfolio/fitness.png" },
    { title: "Bella Vista Elegance", url: "https://bella-vista-elegance.vercel.app/", image: "/portfolio/bellavista.png" },
    { title: "Lumière Elegance", url: "https://lumi-re-elegance.vercel.app/", image: "/portfolio/lumiere.png" },
    { title: "Analytics App Design", url: "#", image: MOCKUP_IMAGES[0] }
  ];

  const steps = [
    { num: "01", title: "Strategy & Planning", text: "Defining the blueprint for your digital success." },
    { num: "02", title: "Design & Development", text: "Crafting pixel-perfect, high-performance experiences." },
    { num: "03", title: "Launch & Growth", text: "Deploying your site and scaling your reach." },
    { num: "04", title: "Ongoing Support", text: "Continuous optimization to ensure maximum conversion." }
  ];

  return (
    <section id="portfolio" className="py-24 md:py-36 overflow-hidden relative z-10 bg-[#0B0B0D]" data-testid="portfolio-section">
      <div className="max-w-7xl mx-auto px-6 mb-16 md:mb-24 text-center flex flex-col items-center">
        <AnimatedSection>
          <motion.h2
            variants={fadeInUp}
            className="text-4xl md:text-5xl lg:text-7xl font-bold text-white mb-6 tracking-tight font-sans"
          >
            Quality Over Volume
          </motion.h2>
          <motion.p
            variants={fadeInUp}
            className="text-[#A0A0A0] text-lg md:text-xl max-w-2xl mx-auto leading-relaxed mb-8"
          >
            Carefully crafted websites built to drive real business results.
          </motion.p>


        </AnimatedSection>
      </div>

      {/* 5-Website Curved Layout (Perspective 3D) */}
      <div className="relative w-full max-w-[1500px] mx-auto h-[500px] sm:h-[550px] md:h-[650px] lg:h-[700px] flex items-center justify-center mt-12 mb-32 md:mb-40 perspective-1000">
        <AnimatedSection className="w-full h-full relative flex items-center justify-center" style={{ perspective: "1200px" }}>

          {/* Site 1 (Far Left) */}
          <motion.a
            href={portfolios[0].url}
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, x: -160, y: -20, scale: 0.75, rotateZ: -10, rotateY: 25 }}
            whileInView={{ opacity: 1, x: -100, y: 0, scale: 0.8, rotateZ: -8, rotateY: 20 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            viewport={{ once: true, margin: "-50px" }}
            className="absolute left-[-15%] md:left-[0%] lg:left-[5%] w-[40%] md:w-[25%] lg:w-[20%] z-[1] hover:z-50 hover:scale-[0.85] hover:-translate-y-4 hover:rotateZ-0 hover:rotateY-0 transition-all duration-500 ease-out rounded-[24px] overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,0.8)] block opacity-60 hover:opacity-100"
            style={{ transformStyle: "preserve-3d" }}
          >
            <img src={portfolios[0].image} alt={portfolios[0].title} loading="lazy" className="w-full h-[300px] md:h-[450px] object-cover rounded-[24px]" />
          </motion.a>

          {/* Site 2 (Mid Left) */}
          <motion.a
            href={portfolios[1].url}
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, x: -100, y: 30, scale: 0.85, rotateZ: -6, rotateY: 15 }}
            whileInView={{ opacity: 1, x: -40, y: 40, scale: 0.9, rotateZ: -4, rotateY: 10 }}
            transition={{ duration: 0.8, delay: 0.1, ease: "easeOut" }}
            viewport={{ once: true, margin: "-50px" }}
            className="absolute left-[5%] md:left-[15%] lg:left-[22%] w-[45%] md:w-[30%] lg:w-[25%] z-[2] hover:z-50 hover:scale-[0.95] hover:-translate-y-4 hover:rotateZ-0 hover:rotateY-0 transition-all duration-500 ease-out rounded-[24px] overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,0.8)] block group opacity-80 hover:opacity-100"
            style={{ transformStyle: "preserve-3d" }}
          >
            <div className="absolute inset-0 bg-black/40 group-hover:bg-transparent transition-colors duration-500 z-10 flex items-center justify-center">
              <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/80 text-white px-4 py-2 rounded-full text-sm font-semibold border border-white/20 backdrop-blur items-center gap-2 flex">
                Visit <ArrowUpRight className="w-3 h-3" />
              </span>
            </div>
            <img src={portfolios[1].image} alt={portfolios[1].title} loading="lazy" className="w-full h-[350px] md:h-[500px] object-cover object-top rounded-[24px]" />
          </motion.a>

          {/* Site 4 (Mid Right) */}
          <motion.a
            href={portfolios[3].url}
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, x: 100, y: 30, scale: 0.85, rotateZ: 6, rotateY: -15 }}
            whileInView={{ opacity: 1, x: 40, y: 40, scale: 0.9, rotateZ: 4, rotateY: -10 }}
            transition={{ duration: 0.8, delay: 0.1, ease: "easeOut" }}
            viewport={{ once: true, margin: "-50px" }}
            className="absolute right-[5%] md:right-[15%] lg:right-[22%] w-[45%] md:w-[30%] lg:w-[25%] z-[2] hover:z-50 hover:scale-[0.95] hover:-translate-y-4 hover:rotateZ-0 hover:rotateY-0 transition-all duration-500 ease-out rounded-[24px] overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,0.8)] block group opacity-80 hover:opacity-100"
            style={{ transformStyle: "preserve-3d" }}
          >
            <div className="absolute inset-0 bg-black/40 group-hover:bg-transparent transition-colors duration-500 z-10 flex items-center justify-center">
              <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/80 text-white px-4 py-2 rounded-full text-sm font-semibold border border-white/20 backdrop-blur items-center gap-2 flex">
                Visit <ArrowUpRight className="w-3 h-3" />
              </span>
            </div>
            <img src={portfolios[3].image} alt={portfolios[3].title} loading="lazy" className="w-full h-[350px] md:h-[500px] object-cover object-top rounded-[24px]" />
          </motion.a>

          {/* Site 5 (Far Right) */}
          <motion.a
            href={portfolios[4].url}
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, x: 160, y: -20, scale: 0.75, rotateZ: 10, rotateY: -25 }}
            whileInView={{ opacity: 1, x: 100, y: 0, scale: 0.8, rotateZ: 8, rotateY: -20 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            viewport={{ once: true, margin: "-50px" }}
            className="absolute right-[-15%] md:right-[0%] lg:right-[5%] w-[40%] md:w-[25%] lg:w-[20%] z-[1] hover:z-50 hover:scale-[0.85] hover:-translate-y-4 hover:rotateZ-0 hover:rotateY-0 transition-all duration-500 ease-out rounded-[24px] overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,0.8)] block opacity-60 hover:opacity-100"
            style={{ transformStyle: "preserve-3d" }}
          >
            <img src={portfolios[4].image} alt={portfolios[4].title} loading="lazy" className="w-full h-[300px] md:h-[450px] object-cover rounded-[24px]" />
          </motion.a>

          {/* Site 3 (Center, foremost) */}
          <motion.a
            href={portfolios[2].url}
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, y: 80, scale: 0.95 }}
            whileInView={{ opacity: 1, y: 70, scale: 1.05 }}
            transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
            viewport={{ once: true, margin: "-50px" }}
            className="absolute z-[10] w-[55%] md:w-[35%] lg:w-[30%] hover:z-50 hover:scale-[1.1] hover:-translate-y-2 transition-all duration-500 ease-out rounded-[24px] overflow-hidden shadow-[0_30px_60px_rgba(0,0,0,0.9)] border border-white/5 block group"
            style={{ transformStyle: "preserve-3d" }}
          >
            <div className="absolute inset-0 bg-black/20 group-hover:bg-transparent transition-colors duration-500 z-10 flex items-center justify-center">
              <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-[#FF5A1F] text-white px-5 py-2.5 rounded-full font-semibold border border-[#FF5A1F] shadow-[0_0_20px_rgba(255,90,31,0.5)] items-center gap-2 flex">
                Visit Site <ArrowUpRight className="w-4 h-4" />
              </span>
            </div>
            <img src={portfolios[2].image} alt={portfolios[2].title} loading="lazy" className="w-full h-[400px] md:h-[550px] object-cover object-top rounded-[24px]" />
          </motion.a>

        </AnimatedSection>
      </div>

      {/* 4 Steps Below Gallery */}
      <div className="max-w-6xl mx-auto px-6">
        <AnimatedSection className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-4 text-center">
          {steps.map((step, idx) => (
            <motion.div key={idx} variants={fadeInUp} className="flex flex-col items-center">
              <span className="text-[#FF5A1F] font-mono text-sm font-semibold mb-3">#{step.num}</span>
              <h4 className="text-white font-semibold text-lg md:text-xl mb-3">{step.title}</h4>
              <p className="text-[#A0A0A0] text-sm leading-relaxed max-w-[200px]">{step.text}</p>
            </motion.div>
          ))}
        </AnimatedSection>
      </div>
    </section>
  );
};

// ===== FINAL CTA SECTION =====
const FinalCTASection = () => {
  const navigate = useNavigate();

  return (
    <section id="cta-final" className="relative py-24 md:py-36 px-6 overflow-hidden section-dark" data-testid="final-cta-section">
      {/* Background with B&W project screenshots */}
      <div className="absolute inset-0 opacity-[0.07]">
        <div className="grid grid-cols-3 gap-4 h-full">
          {MOCKUP_IMAGES.map((img, i) => (
            <div key={i} className="overflow-hidden">
              <img src={img} alt="" className="w-full h-full object-cover grayscale" />
            </div>
          ))}
        </div>
      </div>

      {/* Gradient overlay with warm tones */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0D] via-[#0B0B0D]/95 to-[#0B0B0D]/90" />

      {/* Intense orange glow */}
      <div className="absolute top-0 left-1/2 -translate-x-[50%] -translate-y-[20%] w-[1200px] h-[1200px] bg-[radial-gradient(ellipse_at_center,_rgba(255,85,0,0.4)_0%,_rgba(220,40,0,0.2)_40%,_rgba(120,15,10,0.05)_70%,_transparent_100%)] blur-[100px] pointer-events-none" />

      {/* Grain Overlay */}
      <div className="bg-grain opacity-[0.05]" />

      <div className="relative max-w-4xl mx-auto text-center">
        <AnimatedSection>
          {/* Subtle Pricing Anchor */}
          <motion.div variants={fadeInUp} className="inline-block mb-10 px-4 py-2 border border-white/[0.08] rounded-full bg-white/[0.02] backdrop-blur-sm">
            <p className="text-[#A0A0A0] text-sm font-medium tracking-wide">
              Projects typically start at $500 (₹40,000+) depending on scope.
            </p>
          </motion.div>

          <motion.h2
            variants={fadeInUp}
            className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-7 tracking-[-0.02em] font-sans uppercase leading-[1.1]"
            data-testid="final-cta-headline"
          >
            Ready to Turn Your Website Into a Growth Engine?
          </motion.h2>

          <motion.p
            variants={fadeInUp}
            className="text-[#A0A0A0] text-lg md:text-xl max-w-2xl mx-auto mb-10 leading-relaxed"
          >
            Let's build something that positions your brand, captures intent, and drives real business results — not just traffic.
          </motion.p>

          <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row items-center justify-center gap-4 md:gap-6 mt-12">
            <button
              onClick={() => navigate("/strategy")}
              className="cta-premium px-8 py-4 md:px-12 md:py-5 text-white text-lg font-semibold rounded-[24px] inline-flex items-center justify-center gap-3 w-full sm:w-auto"
              data-testid="final-cta-button"
            >
              Book a Strategy Call
              <ArrowUpRight className="w-6 h-6" />
            </button>
          </motion.div>
        </AnimatedSection>
      </div>
    </section>
  );
};

// ===== FOOTER =====
const Footer = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleNavClick = (id) => {
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <footer className="py-20 px-6 border-t border-white/[0.04] section-dark" data-testid="footer">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-4 gap-12 mb-16">
          {/* Logo & Description */}
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-9 h-9 bg-gradient-to-br from-[#FF5A1F] to-[#E64D15] rounded-xl flex items-center justify-center shadow-lg shadow-[#FF5A1F]/20">
                <span className="text-white font-bold text-sm">AS</span>
              </div>
              <span className="text-[#F8F8F8] font-semibold text-lg">Aligned Studio</span>
            </div>
            <p className="text-[#6B6B6B] max-w-sm leading-relaxed">
              Strategic web design for SaaS and service brands that want websites that actually convert.
            </p>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="text-[#F8F8F8] font-semibold mb-5">Navigate</h4>
            <ul className="space-y-3">
              {["About", "Portfolio", "FAQs"].map((item) => (
                <li key={item}>
                  <button
                    onClick={() => handleNavClick(item.toLowerCase())}
                    className="footer-link-premium text-[#6B6B6B] hover:text-[#F8F8F8] transition-colors"
                  >
                    {item}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-[#F8F8F8] font-semibold mb-5">Connect</h4>
            <div className="space-y-4">
              <a href="mailto:workxmanish@gmail.com" className="flex items-center gap-2 text-[#6B6B6B] hover:text-[#F8F8F8] transition-colors">
                <Mail className="w-4 h-4" />
                workxmanish@gmail.com
              </a>
              <div className="flex items-center gap-4">
                <a href="https://www.linkedin.com/in/manish-kumar-371170293/" target="_blank" rel="noopener noreferrer" className="text-[#6B6B6B] hover:text-[#F8F8F8] transition-colors">
                  <Linkedin className="w-5 h-5" />
                </a>
                <a href="https://www.instagram.com/manish__6582/" target="_blank" rel="noopener noreferrer" className="text-[#6B6B6B] hover:text-[#F8F8F8] transition-colors">
                  <Instagram className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="flex flex-col md:flex-row items-center justify-between pt-8 border-t border-white/[0.04] gap-4">
          <p className="text-[#6B6B6B] text-sm">
            &copy; {new Date().getFullYear()} Aligned Studio. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            {["Privacy Policy", "Terms of Service", "Cookie Settings"].map((link) => (
              <a key={link} href="#" className="text-[#6B6B6B] text-sm hover:text-[#F8F8F8] transition-colors">
                {link}
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

// ===== HOME PAGE CONTENT =====
const Home = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main>
      <HeroSection />
      <WorkMarquee />
      <TechStackBar />
      <ProblemSection />
      <IndustriesSection />
      <SolutionSection />
      <ProcessSection />
      <MidPageCTA />
      <QualityProcessSection />
      <FAQSection />
      <PortfolioSection />
      <FinalCTASection />
    </main>
  );
};


// ===== MAIN APP =====
function App() {
  return (
    <>
      <Router>
        <div className="min-h-screen bg-[#0B0B0D] relative overflow-hidden w-full max-w-[100%]">
          <div className="bg-grid-pattern" />
          <div className="ambient-sphere sphere-1" />
          <div className="ambient-sphere sphere-2" />
          <div className="ambient-sphere sphere-3" />
          <div className="noise-overlay" />
          <Toaster position="top-right" theme="dark" richColors />
          <Navbar />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/saas" element={<SaaSPage />} />
            <Route path="/b2b" element={<B2BPage />} />
            <Route path="/startups" element={<StartupsPage />} />
            <Route path="/ecommerce" element={<EcommercePage />} />
            <Route path="/strategy" element={<StrategyPage />} />
          </Routes>
          <Footer />
        </div>
      </Router>
    </>
  );
}

export default App;
// Force HMR reload
